import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  OrderChatMessage,
  OrderChatMeta,
  OrderChatSenderRole,
  OrderChatService,
  OrderChatUserBrief,
} from '../../services/order-chat.service';

@Component({
  selector: 'app-order-chat-panel',
  templateUrl: './order-chat-panel.component.html',
  styleUrls: ['./order-chat-panel.component.scss'],
})
export class OrderChatPanelComponent implements OnChanges, OnDestroy {
  @Input() orderId = '';
  @Input() customerId: string | null = null;
  @Input() providerId: string | null = null;
  @Input() companyId: string | null = null;
  @Input() orderStatus: string | null = null;
  @Input() customerName: string | null = null;
  @Input() providerName: string | null = null;
  /** Company/admin: view history; messaging stays customer↔provider. */
  @Input() readOnly = true;
  /** Admin: clickable names + user detail dialog + profile link. */
  @Input() adminControls = false;
  @Input() peerTitle = '';

  @ViewChild('scrollBox') scrollBox?: ElementRef<HTMLDivElement>;

  messages: OrderChatMessage[] = [];
  meta: OrderChatMeta | null = null;
  loading = true;
  error = '';
  myUid: string | null = null;
  myRole: OrderChatSenderRole = 'company';
  myName = '';

  customerBrief: OrderChatUserBrief | null = null;
  providerBrief: OrderChatUserBrief | null = null;
  private briefsByUid = new Map<string, OrderChatUserBrief>();

  inspectVisible = false;
  inspectLoading = false;
  inspectBrief: OrderChatUserBrief | null = null;
  copyHint = '';

  private subs: Subscription[] = [];

  constructor(private chat: OrderChatService, private router: Router) {}

  get isClosed(): boolean {
    return (
      this.meta?.status === 'closed' ||
      ['done', 'cancelled', 'canceled', 'failed'].includes(
        String(this.orderStatus || '').toLowerCase()
      )
    );
  }

  get isAdminView(): boolean {
    return this.adminControls || this.myRole === 'admin';
  }

  get displayCustomerName(): string {
    return (
      this.customerBrief?.name ||
      String(this.customerName || '').trim() ||
      'Customer'
    );
  }

  get displayProviderName(): string {
    return (
      this.providerBrief?.name ||
      String(this.providerName || '').trim() ||
      'Provider'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['orderId'] ||
      changes['orderStatus'] ||
      changes['customerId'] ||
      changes['providerId']
    ) {
      void this.boot();
    }
  }

  ngOnDestroy(): void {
    this.clearSubs();
  }

  private clearSubs(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  private async boot(): Promise<void> {
    this.clearSubs();
    this.messages = [];
    this.meta = null;
    this.error = '';
    this.loading = true;
    this.customerBrief = null;
    this.providerBrief = null;
    this.briefsByUid.clear();
    const orderId = String(this.orderId || '').trim();
    if (!orderId) {
      this.loading = false;
      return;
    }

    try {
      const profile = await this.chat.resolveSenderProfile();
      this.myUid = profile.uid;
      this.myRole = profile.role;
      this.myName = profile.name;

      await this.chat.requestStaffAccess(orderId);
      await this.loadParticipants();

      this.subs.push(
        this.chat.watchMeta(orderId).subscribe((m) => {
          this.meta = m;
        })
      );
      this.subs.push(
        this.chat.watchMessages(orderId).subscribe((list) => {
          this.messages = list;
          this.loading = false;
          void this.ensureMessageSenders(list);
          setTimeout(() => this.scrollToBottom(), 50);
        })
      );
    } catch (e: any) {
      this.loading = false;
      this.error = e?.error?.error || e?.message || 'Could not open chat';
    }
  }

  private async loadParticipants(): Promise<void> {
    const customerId =
      String(this.customerId || this.meta?.customerId || '').trim();
    const providerId =
      String(this.providerId || this.meta?.providerId || '').trim();

    const [c, p] = await Promise.all([
      customerId ? this.chat.loadUserBrief(customerId) : Promise.resolve(null),
      providerId ? this.chat.loadUserBrief(providerId) : Promise.resolve(null),
    ]);

    if (c) {
      if (
        String(this.customerName || '').trim() &&
        (c.name === 'Unknown user' || c.name === 'Unknown')
      ) {
        c.name = String(this.customerName).trim();
      }
      this.customerBrief = c;
      this.briefsByUid.set(c.uid, c);
    }
    if (p) {
      if (
        String(this.providerName || '').trim() &&
        (p.name === 'Unknown user' || p.name === 'Unknown')
      ) {
        p.name = String(this.providerName).trim();
      }
      this.providerBrief = p;
      this.briefsByUid.set(p.uid, p);
    }
  }

  private async ensureMessageSenders(list: OrderChatMessage[]): Promise<void> {
    const missing = Array.from(
      new Set(
        list
          .map((m) => String(m.senderId || '').trim())
          .filter((id) => id && !this.briefsByUid.has(id))
      )
    );
    await Promise.all(
      missing.map(async (uid) => {
        const brief = await this.chat.loadUserBrief(uid);
        if (brief) this.briefsByUid.set(uid, brief);
      })
    );
  }

  displayName(msg: OrderChatMessage): string {
    const uid = String(msg.senderId || '').trim();
    const brief = uid ? this.briefsByUid.get(uid) : null;
    if (brief?.name && brief.name.toLowerCase() !== 'user') {
      return brief.name;
    }
    if (uid && this.customerBrief?.uid === uid) {
      return this.displayCustomerName;
    }
    if (uid && this.providerBrief?.uid === uid) {
      return this.displayProviderName;
    }
    const stored = String(msg.senderName || '').trim();
    if (stored && stored.toLowerCase() !== 'user') {
      return stored;
    }
    const role = String(msg.senderRole || '').toLowerCase();
    if (role === 'customer') return this.displayCustomerName;
    if (role === 'provider') return this.displayProviderName;
    return this.chat.roleLabel(role || 'user');
  }

  roleBadge(msg: OrderChatMessage): string {
    const uid = String(msg.senderId || '').trim();
    if (uid && this.customerBrief?.uid === uid) return 'Customer';
    if (uid && this.providerBrief?.uid === uid) return 'Provider';
    return this.chat.roleLabel(String(msg.senderRole || 'user'));
  }

  bubbleSide(msg: OrderChatMessage): 'customer' | 'provider' | 'other' {
    const uid = String(msg.senderId || '').trim();
    const role = String(msg.senderRole || '').toLowerCase();
    if (uid && this.customerBrief?.uid === uid) return 'customer';
    if (uid && this.providerBrief?.uid === uid) return 'provider';
    if (role === 'customer') return 'customer';
    if (role === 'provider') return 'provider';
    return 'other';
  }

  isMine(msg: OrderChatMessage): boolean {
    return !!this.myUid && msg.senderId === this.myUid;
  }

  formatTime(ts: number | object | null | undefined): string {
    const n = typeof ts === 'number' ? ts : 0;
    if (!n) return '';
    try {
      return new Date(n).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }

  openParticipant(which: 'customer' | 'provider'): void {
    if (!this.isAdminView) return;
    const brief =
      which === 'customer' ? this.customerBrief : this.providerBrief;
    const uid =
      which === 'customer'
        ? String(this.customerId || this.meta?.customerId || '').trim()
        : String(this.providerId || this.meta?.providerId || '').trim();
    void this.openUserInspect(brief?.uid || uid, which);
  }

  openMessageSender(msg: OrderChatMessage): void {
    if (!this.isAdminView) return;
    const uid = String(msg.senderId || '').trim();
    if (!uid) return;
    void this.openUserInspect(uid);
  }

  private async openUserInspect(
    uid: string,
    fallbackRole?: 'customer' | 'provider'
  ): Promise<void> {
    this.inspectVisible = true;
    this.inspectLoading = true;
    this.copyHint = '';
    this.inspectBrief = this.briefsByUid.get(uid) || null;
    try {
      const brief = await this.chat.loadUserBrief(uid);
      if (brief) {
        if (
          fallbackRole === 'customer' &&
          String(this.customerName || '').trim() &&
          (brief.name === 'Unknown user' || brief.name === 'Unknown')
        ) {
          brief.name = String(this.customerName).trim();
        }
        if (
          fallbackRole === 'provider' &&
          String(this.providerName || '').trim() &&
          (brief.name === 'Unknown user' || brief.name === 'Unknown')
        ) {
          brief.name = String(this.providerName).trim();
        }
        this.inspectBrief = brief;
        this.briefsByUid.set(uid, brief);
      }
    } finally {
      this.inspectLoading = false;
    }
  }

  async copyUid(): Promise<void> {
    const uid = this.inspectBrief?.uid;
    if (!uid) return;
    try {
      await navigator.clipboard.writeText(uid);
      this.copyHint = 'Copied';
      setTimeout(() => (this.copyHint = ''), 1500);
    } catch {
      this.copyHint = 'Copy failed';
    }
  }

  openFullProfile(): void {
    if (!this.inspectBrief) return;
    const path = this.chat.adminProfilePath(this.inspectBrief);
    this.inspectVisible = false;
    void this.router.navigateByUrl(path);
  }

  private scrollToBottom(): void {
    const el = this.scrollBox?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }
}
